import { Dialog, DialogContent } from "@cmp-src/primitives/Dialog";
import { canScheduleOthers } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { cn } from "@v2/lib/utils";
import { useMapModalContext } from "@webV2/components/map-modal";
import { useRouterPush } from "@webV2/helpers/router/useRouterPush";
import { useEffect, useMemo, useRef } from "react";
import { GlobalSearchControls } from "./GlobalSearchControls";
import { useGlobalSearchData } from "./GlobalSearchDataProvider";
import { useGlobalSearch } from "./GlobalSearchProvider";
import { EmptyState } from "./components/EmptyState";
import { KeyboardHints } from "./components/KeyboardHints";
import { SearchInput } from "./components/SearchInput";
import { SearchResults } from "./components/SearchResults";
import { INPUT_FOCUS_DELAY_MS } from "./constants";
import { useDivisionCategories } from "./hooks/useDivisionCategories";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { useSearchItemSelection } from "./hooks/useSearchItemSelection";
import { useSearchNavigation } from "./hooks/useSearchNavigation";
import { useSearchResults } from "./hooks/useSearchResults";

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const { setSearchQuery: setQuery, searchQuery: query } = useGlobalSearch();
  const inputRef = useRef<HTMLInputElement>(null);
  const routerPush = useRouterPush();

  const {
    userContext: { currentOffice, authenticatedUser }
  } = useAuthenticated();

  const isAdmin = useMemo(
    () => canScheduleOthers(authenticatedUser),
    [authenticatedUser]
  );

  const {
    resources,
    people,
    offices,
    schedulesByUser,
    schedulesByResource,
    loading,
    hasNoResults: hasNoApiResults
  } = useGlobalSearchData();

  const divisionCategories = useDivisionCategories();

  const {
    filteredSettings,
    filteredActions,
    filteredOffices,
    hasNoResults,
    totalResults
  } = useSearchResults({
    query,
    resources,
    people,
    offices,
    hasNoApiResults,
    isAdmin,
    authenticatedUser
  });

  const navigation = useSearchNavigation({
    resources,
    offices,
    divisionCategories,
    currentOffice,
    onClose
  });

  const { handleSelectItem, handleSelectSetting } = useSearchItemSelection({
    filteredActions,
    resources,
    people,
    filteredOffices,
    filteredSettings,
    navigation
  });

  const { activeIndex, handleKeyDown, registerItemRef } = useKeyboardNavigation(
    {
      totalResults,
      hasNoResults,
      onSelect: handleSelectItem,
      onClose,
      onNoResultsAction: () => navigation.askTessaWithContext(query),
      isOpen
    }
  );

  const { closeModal: closeMapModal } = useMapModalContext();

  useEffect(() => {
    if (isOpen) {
      closeMapModal();
      setTimeout(() => inputRef.current?.focus(), INPUT_FOCUS_DELAY_MS);
    }
  }, [isOpen, closeMapModal]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex h-[80vh] max-h-[80vh] max-w-2xl flex-col gap-0 overflow-hidden p-0"
        overlayProps={{ className: "bg-black/50" }}
      >
        <div className="flex h-full min-h-0 flex-col" onKeyDown={handleKeyDown}>
          <div className="flex-shrink-0 border-gray-200 border-b">
            <SearchInput
              value={query}
              onChange={setQuery}
              inputRef={inputRef}
            />
          </div>

          <GlobalSearchControls />

          <ScrollArea.Root
            type="hover"
            scrollHideDelay={500}
            className="flex-1 overflow-hidden"
          >
            <ScrollArea.Viewport className="[&>div]:!block h-full w-full">
              <div className="p-2">
                {loading || hasNoResults ? (
                  <EmptyState isLoading={loading} />
                ) : (
                  <SearchResults
                    filteredActions={filteredActions}
                    resources={resources}
                    people={people}
                    filteredOffices={filteredOffices}
                    filteredSettings={filteredSettings}
                    schedulesByResource={schedulesByResource}
                    schedulesByUser={schedulesByUser}
                    divisionCategories={divisionCategories}
                    activeIndex={activeIndex}
                    registerItemRef={registerItemRef}
                    onNavigateToResource={navigation.navigateToResource}
                    onNavigateToPerson={navigation.navigateToPerson}
                    onNavigateToOffice={navigation.navigateToOffice}
                    onEditOffice={async (officeId) => {
                      await routerPush(`/settings/offices/${officeId}`);
                      onClose();
                    }}
                    onReserveOffice={navigation.reserveOffice}
                    onSelectSetting={handleSelectSetting}
                  />
                )}
              </div>
            </ScrollArea.Viewport>

            <ScrollArea.Scrollbar
              orientation="vertical"
              className={cn(
                "z-50 flex w-3 bg-gray-50/50 p-[2px] hover:bg-gray-100"
              )}
            >
              <ScrollArea.Thumb className="relative w-[4px] flex-1 rounded-full bg-gray-300 hover:bg-gray-400" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>

          <KeyboardHints />
        </div>
      </DialogContent>
    </Dialog>
  );
}
